import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, Button, ButtonBase, Card, darken, Dialog, DialogActions, DialogContent, DialogTitle, lighten, Typography, useTheme } from '@mui/material';
import { useKrTheme } from '@/common/theme/KrThemeContext';
import { StringUtils } from '@/common/utils/StringUtils';
import { useMemo, useState } from 'react';

export const PivotColsDialog = ({ columns, rows, value, closeDialog }) => {

  const [colCodes, setColCodes] = useState(columns);
  const [rowCodes, setRowCodes] = useState(rows);
  const [valueCodes, setValueCodes] = useState(value);

  const [draggingId, setDraggingId] = useState(null);

  const submit = () => {
    closeDialog({
      columns: colCodes,
      rows: rowCodes,
      value: valueCodes,
    });
  }

  // Track which item is being dragged so we can disable the "values" list
  const handleDragStart = (start) => {
    setDraggingId(start.draggableId);
  };

  const handleDragEnd = ({ source, destination }) => {

    setDraggingId(null);

    // Dropped outside of any list
    if (!destination) {
      return;
    }

    // Source
    const sourceList = source.droppableId === 'columns' ? colCodes : source.droppableId === 'rows' ? rowCodes : valueCodes;
    const setSource = source.droppableId === 'columns' ? setColCodes : source.droppableId === 'rows' ? setRowCodes : setValueCodes;

    const newSource = Array.from(sourceList);
    const [sourceMoved] = newSource.splice(source.index, 1);

    // Move in the same list
    if (source.droppableId === destination.droppableId) {
      newSource.splice(destination.index, 0, sourceMoved);
      setSource(newSource);
      return;
    }

    // Destination
    const destList = destination.droppableId === 'columns' ? colCodes : destination.droppableId === 'rows' ? rowCodes : valueCodes;
    const setDest = destination.droppableId === 'columns' ? setColCodes : destination.droppableId === 'rows' ? setRowCodes : setValueCodes;

    // Remove from source
    setSource(newSource);

    // Add to destination
    const newDest = Array.from(destList);
    newDest.splice(destination.index, 0, sourceMoved);
    setDest(newDest);
  }

  const theme = useTheme();
  const { isDarkMode } = useKrTheme();

  const allColsById = useMemo(() => {
    const map = new Map();
    [...colCodes, ...rowCodes, ...valueCodes].forEach(code => map.set(code.displayName, code));
    return map;
  }, []);

  const renderDropColumn = (id, cols) => {
    // Disable the "values" list while a non-value item is being dragged
    const draggingItem = id === 'values' && !!draggingId ? allColsById.get(draggingId) : null;
    const disableDrop = !!draggingItem && draggingItem.isValue === false;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0',
          minWidth: 0,
          minHeight: 0
        }}
      >
        <Typography align="center" fontWeight="normal" variant="subtitle1" >{`${StringUtils.toUpperCamel(id)}`}</Typography>
        <Droppable droppableId={id} isDropDisabled={disableDrop}>
          {(provided, snapshot) => {
            const { isDraggingOver } = snapshot;
            
            const bgColor = isDarkMode
              ? isDraggingOver ? lighten(theme.palette.surface.main, 0.05) : theme.palette.surface.main
              : isDraggingOver ? darken(theme.palette.primary.light, 0.05) : theme.palette.primary.light

            return (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  backgroundColor: bgColor,
                  borderRadius: 1,
                  p: 1,
                  opacity: disableDrop ? 0.5 : 1,
                  cursor: disableDrop ? 'not-allowed' : 'grab',
                  filter: disableDrop ? 'grayscale(0.6)' : 'none',
                  transition: 'background-color 0.2s, opacity 0.2s'
                }}
              >
                {cols.map((col, index) => (
                  <Draggable key={col.field} draggableId={col.displayName} index={index}>
                    {(prov, snapshot) => (
                      <ButtonBase
                        component={Card}
                        focusRipple
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        sx={{
                          display: 'block',
                          width: '100%',
                          borderRadius: 1,
                          boxShadow: 2,
                          mb: 1,
                          p: 1.5,
                          bgcolor: snapshot.isDragging
                            ? darken(theme.palette.primary.main, 0.2)
                            : theme.palette.primary.main,
                          cursor: 'grab',
                          transition: 'background-color 120ms',
                            '&:hover': {
                              bgcolor: darken(theme.palette.primary.main, 0.1),
                            },
                        }}
                      >
                        <Typography
                          sx={{
                            textAlign: 'center',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            maxWidth: '100%',
                            overflowWrap: 'anywhere',
                            lineHeight: 1.2,
                          }}
                          title={col.displayName}
                        >
                          {col.displayName}
                        </Typography>
                      </ButtonBase>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            );
          }}
        </Droppable>
      </Box>
    );
  };

  return (
    <Dialog open fullWidth maxWidth="md" data-test-id="pivot-cols-dialog">
      <DialogTitle>Rearrange Columns</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          overflow: 'hidden'
        }}
      >
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              padding: 2,
              minHeight: 0,
            }}
          >
            {/* Columns */}
            {renderDropColumn('columns', colCodes)}

            {/* Rows */}
            {renderDropColumn('rows', rowCodes)}

            {/* Value */}
            {renderDropColumn('values', valueCodes)}
          </Box>
        </DragDropContext>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => closeDialog()}>Cancel</Button>
        <Button onClick={submit} data-test-id="pivot-cols-submit-button">Submit</Button>
      </DialogActions>

    </Dialog>

  );
}